/**
 * Token Authentication Utilities
 *
 * Handles JWT token parsing, validation, and verification for token-based authentication.
 * Token is passed via URL query parameter (?token=...)
 *
 * Features:
 * - JWT signature verification using RSA public key
 * - Token expiration checking
 * - Required field validation
 */
/* eslint-disable no-console */

import { jwtVerify } from 'jose';
import type { TokenData } from '@ox/visits-ui/types';
import { JWT_SECRET_KEY } from '@ox/visits-ui/config/auth';

/**
 * Verify JWT signature and extract payload
 * Uses RSA public key to verify token was signed by backend
 */
export async function verifyJWT(token: string): Promise<TokenData | null> {
  const { payload } = await jwtVerify(token, JWT_SECRET_KEY);
  return validateTokenData(payload);
}

/**
 * Parse JWT token without verification (for development/debugging)
 * Note: This does NOT verify the signature - use verifyJWT() for secure verification
 */
export function parseJWT(token: string): Record<string, unknown> | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[Token Auth] Invalid JWT format - expected 3 parts');
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error('[Token Auth] Failed to parse JWT:', err);
    return null;
  }
}

/**
 * Validate that token payload contains required fields and correct types
 */
export function validateTokenData(payload: Record<string, unknown>): TokenData | null {
  // Check for created_at (should be a number/timestamp)
  if (!payload.created_at || typeof payload.created_at !== 'number') {
    throw new Error('[Token Auth] Missing or invalid created_at timestamp');
  }

  // Check for exp (JWT standard expiration field)
  if (!payload.exp || typeof payload.exp !== 'number') {
    throw new Error('[Token Auth] Missing or invalid exp timestamp');
  }

  // Check if token is expired
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  if (payload.exp < now) {
    throw new Error('[Token Auth] Token has expired');
  }

  return {
    user_email: payload.user_email,
    first_name: payload.first_name,
    last_name: payload.last_name,
    visit_form_id: payload.visit_form_id,
    listing_id: payload.listing_id,
    property_address: payload.property_address,
    created_at: payload.created_at,
    exp: payload.exp,
  };
}

/**
 * Get token from URL query parameters
 */
export function getTokenFromURL(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  if (!token) {
    console.error('[Token Auth] No token found in URL query parameters');
    return null;
  }

  return token;
}

/**
 * Verify and validate token from URL
 * Returns TokenData if valid, null if invalid/missing
 *
 * This is an async function that verifies the JWT signature.
 */
export async function getAuthDataFromURL(): Promise<TokenData | null> {
  const token = getTokenFromURL();
  if (!token) {
    return null;
  }

  const tokenData = await verifyJWT(token);
  if (!tokenData) {
    return null;
  }

  console.log('[Token Auth] Successfully verified token for:', tokenData.user_email);
  console.log('[Token Auth] Token expires:', new Date(tokenData.exp * 1000).toLocaleString());
  return tokenData;
}
